import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const DishFilters = ({ filters, onFiltersChange, resultCount }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const cuisineTypes = [
    'Italian', 'French', 'Asian', 'Mexican', 'Indian', 'Mediterranean', 'American', 'Thai', 'Japanese', 'Greek'
  ];

  const dietaryRestrictions = [
    { id: 'vegetarian', label: 'Vegetarian', icon: 'Leaf' },
    { id: 'vegan', label: 'Vegan', icon: 'Sprout' },
    { id: 'gluten-free', label: 'Gluten-Free', icon: 'Wheat' },
    { id: 'dairy-free', label: 'Dairy-Free', icon: 'Milk' },
    { id: 'nut-free', label: 'Nut-Free', icon: 'Nut' },
    { id: 'halal', label: 'Halal', icon: 'Moon' },
    { id: 'kosher', label: 'Kosher', icon: 'Star' }
  ];

  const prepTimeRanges = [
    { id: 'quick', label: 'Quick (≤30 min)', min: 0, max: 30 },
    { id: 'medium', label: 'Medium (30-60 min)', min: 30, max: 60 },
    { id: 'long', label: 'Long (60+ min)', min: 60, max: 999 }
  ];

  const handleSearchChange = (e) => {
    onFiltersChange({ ...filters, search: e?.target?.value });
  };

  const handleCuisineToggle = (cuisine) => {
    const newCuisines = filters?.cuisines?.includes(cuisine)
      ? filters?.cuisines?.filter(c => c !== cuisine)
      : [...filters?.cuisines, cuisine];
    onFiltersChange({ ...filters, cuisines: newCuisines });
  };

  const handleDietaryToggle = (restriction) => {
    const newRestrictions = filters?.dietaryRestrictions?.includes(restriction)
      ? filters?.dietaryRestrictions?.filter(r => r !== restriction)
      : [...filters?.dietaryRestrictions, restriction];
    onFiltersChange({ ...filters, dietaryRestrictions: newRestrictions });
  };

  const handlePrepTimeToggle = (range) => {
    const newRanges = filters?.prepTimeRanges?.includes(range?.id)
      ? filters?.prepTimeRanges?.filter(r => r !== range?.id)
      : [...filters?.prepTimeRanges, range?.id];
    onFiltersChange({ ...filters, prepTimeRanges: newRanges });
  };

  const handleServingsChange = (field, value) => {
    onFiltersChange({
      ...filters,
      servings: { ...filters?.servings, [field]: value }
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      search: '',
      cuisines: [],
      dietaryRestrictions: [],
      prepTimeRanges: [],
      servings: { min: '', max: '' },
      sortBy: 'name'
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.search) count++;
    count += filters?.cuisines?.length;
    count += filters?.dietaryRestrictions?.length;
    count += filters?.prepTimeRanges?.length;
    if (filters?.servings?.min || filters?.servings?.max) count++;
    return count;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      {/* Search and Toggle */}
      <div className="flex items-center space-x-4 mb-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search dishes by name or ingredient..."
            value={filters?.search}
            onChange={handleSearchChange}
            className="w-full"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setIsExpanded(!isExpanded)}
          className="lg:hidden"
        >
          <Icon name="Filter" size={16} className="mr-2" />
          Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} className="ml-2" />
        </Button>
      </div>
      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {resultCount} {resultCount === 1 ? 'dish' : 'dishes'} found
        </p>
        {getActiveFilterCount() > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <Icon name="X" size={14} className="mr-1" />
            Clear all
          </Button>
        )}
      </div>
      {/* Advanced Filters */}
      <div className={`space-y-6 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        {/* Cuisine Types */}
        <div>
          <h4 className="font-medium text-foreground mb-3">Cuisine Type</h4>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
            {cuisineTypes?.map((cuisine) => (
              <label key={cuisine} className="flex items-center space-x-2 cursor-pointer">
                <Checkbox
                  checked={filters?.cuisines?.includes(cuisine)}
                  onChange={() => handleCuisineToggle(cuisine)}
                />
                <span className="text-sm text-foreground">{cuisine}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Dietary Restrictions */}
        <div>
          <h4 className="font-medium text-foreground mb-3">Dietary Restrictions</h4>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
            {dietaryRestrictions?.map((restriction) => (
              <label key={restriction?.id} className="flex items-center space-x-2 cursor-pointer">
                <Checkbox
                  checked={filters?.dietaryRestrictions?.includes(restriction?.id)}
                  onChange={() => handleDietaryToggle(restriction?.id)}
                />
                <Icon name={restriction?.icon} size={16} className="text-primary" />
                <span className="text-sm text-foreground">{restriction?.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Preparation Time */}
        <div>
          <h4 className="font-medium text-foreground mb-3">Preparation Time</h4>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
            {prepTimeRanges?.map((range) => (
              <label key={range?.id} className="flex items-center space-x-2 cursor-pointer">
                <Checkbox
                  checked={filters?.prepTimeRanges?.includes(range?.id)}
                  onChange={() => handlePrepTimeToggle(range)}
                />
                <span className="text-sm text-foreground">{range?.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Servings */}
        <div>
          <h4 className="font-medium text-foreground mb-3">Servings</h4>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Input
                type="number"
                placeholder="Min"
                value={filters?.servings?.min}
                onChange={(e) => handleServingsChange('min', e?.target?.value)}
                min="1"
              />
            </div>
            <span className="text-muted-foreground">to</span>
            <div className="flex-1">
              <Input
                type="number"
                placeholder="Max"
                value={filters?.servings?.max}
                onChange={(e) => handleServingsChange('max', e?.target?.value)}
                min="1"
              />
            </div>
          </div>
        </div>

        {/* Sort By */}
        <div>
          <h4 className="font-medium text-foreground mb-3">Sort By</h4>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              { value: 'name', label: 'Name A-Z' },
              { value: 'recent', label: 'Recently Added' },
              { value: 'popular', label: 'Most Used' },
              { value: 'rating', label: 'Highest Rated' }
            ]?.map((option) => (
              <label key={option?.value} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="sortBy"
                  value={option?.value}
                  checked={filters?.sortBy === option?.value}
                  onChange={(e) => onFiltersChange({ ...filters, sortBy: e?.target?.value })}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-sm text-foreground">{option?.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DishFilters;